<template>
  <GhostComponent
    :title="title"
    component-name-when-active="PopperCardButton"
    slot-to-render-when-inactive="btn"
    placement="right"
    strategy="absolute"
    style="display: inline-block"
    global
  >
    <template #btn>
      <BaseLabel basic class="m-r-5 f-11" :text="`#${localId}`" @click="readyToLoadData = true" />
    </template>
    <template #popper>
      <div v-if="$apollo.loading" class="text-center loader">
        <div class="preloader m-20 m-b-0 pls-blue pls-center">
          <svg class="pl-circular" viewBox="25 25 50 50">
            <circle class="plc-path" cx="50" cy="50" r="20"></circle>
          </svg>
        </div>
      </div>
      <template v-if="loaded">
        <!--        <pre>{{ bill.worksomeTimesheet }}</pre>-->
        <template
          v-if="
            bill.dateStart &&
            bill.dateEnd &&
            moment(bill.dateStart).isValid() &&
            moment(bill.dateEnd).isValid()
          "
        >
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Freelancer') }}:</strong>
            </div>
            <div class="col-sm-6">
              <span v-if="bill.freelancer.primaryProfile === null">{{ bill.freelancer.name }}</span>
              <a v-else :href="'/profile/' + bill.freelancer.primaryProfile.localId">
                {{ bill.freelancer.name }}
              </a>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Date from') }}:</strong>
            </div>
            <div class="col-sm-6 nowrap">
              {{ moment(bill.dateStart).format('DD. MMM, Y') }}
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Date to') }}:</strong>
            </div>
            <div class="col-sm-6 nowrap">
              {{ moment(bill.dateEnd).format('DD. MMM, Y') }}
            </div>
          </div>
        </template>

        <div class="row">
          <template v-if="bill.rateType === 'hourly'">
            <div class="col-sm-6 nowrap">
              <strong>{{ overtimeHours > 0 ? $t('Regular hours') : $t('Hours') }}</strong>
            </div>
            <div class="col-sm-6">
              {{ hours || '-' }}
            </div>
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Overtime hours') }}</strong>
            </div>
            <div class="col-sm-6">
              {{ overtimeHours || '-' }}
            </div>
          </template>
          <template v-else-if="bill.rateType === 'daily'">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Days') }}</strong>
            </div>
            <div class="col-sm-6">
              {{ bill.days || '-' }}
            </div>
          </template>
          <div v-else class="col-sm-12">
            <strong>
              {{ bill.rateTypeName ? $t(bill.rateTypeName.capitalize) : '-' }}
            </strong>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Rate') }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.rate }}
          </div>
        </div>
        <hr class="m-t-10 m-b-5" />
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Bill amount') }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.currencySymbol }}&nbsp;{{ bill.totalBilledMoney.inDec }}
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Service fee') }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.currencySymbol }}&nbsp;{{ bill.serviceFeeMoney.inDec }}
          </div>
        </div>
        <div v-if="Number(bill.expenseReportsMoney.inDec.replaceAll(',', '')) > 0" class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Expense report(s)') }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.currencySymbol }}&nbsp;{{ bill.expenseReportsMoney.inDec }}
          </div>
        </div>
        <div v-if="bill.hasRecruiter && bill.recruiterFeeMoney?.inDec > 0" class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Recruiter fee') }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.currencySymbol }}&nbsp;{{ bill.recruiterFeeMoney.inDec }}
          </div>
        </div>
        <div v-if="bill.employmentCostMoney && bill.employmentCostMoney.cents !== 0" class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Employment cost') }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.currencySymbol }}
            {{ bill.employmentCostMoney.inDec }}
          </div>
        </div>
        <div class="row c-indigo">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Total invoice amount') }}</strong>
            :
          </div>
          <div class="col-sm-6 f-500">
            {{ bill.currencySymbol }}&nbsp;{{ bill.totalPayableMoney.inDec }}
          </div>
        </div>
        <hr class="m-t-10 m-b-5" />
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t(`Total incl. ${vatTerminology}`) }}</strong>
            :
          </div>
          <div class="col-sm-6">
            {{ bill.currencySymbol }}&nbsp;{{ bill.totalPayableMoney.withVat.inDec }}
          </div>
        </div>

        <div
          v-if="bill.ymessage && bill.ymessage.body"
          class="bgm-lightgray p-5 m-t-10 f-12"
          style="max-height: 65px; overflow: auto"
        >
          {{ bill.ymessage.body }}
        </div>
        <a
          :href="'/bills/' + bill.localId + '/pdf'"
          :title="$t('Download bill as PDF')"
          target="_blank"
          class="iblock m-t-10"
        >
          <span class="glyphicon glyphicon-file" aria-hidden="true" />
          {{ $t('Download bill') }}
        </a>
      </template>
    </template>
  </GhostComponent>
</template>

<script>
import gql from 'graphql-tag'
import moment from 'moment'
import GhostComponent from '@/components/GhostComponent.vue'
import BaseLabel from '@/components/BaseLabel.vue'

const BILL_FRAGMENT = gql`
  fragment PopoverBillFragment on Bill {
    globalId
    billNumber
    localId
    dateStart
    dateEnd
    rateType
    hours
    days
    rateTypeName
    rate
    currencySymbol
    totalBilledMoney {
      inDec
    }
    serviceFeeMoney {
      inDec
    }
    expenseReportsMoney {
      inDec
    }
    hasRecruiter
    recruiterFeeMoney {
      inDec
    }
    employmentCostMoney {
      cents
      inDec
    }
    totalPayableMoney {
      inDec
      withVat {
        inDec
      }
    }
    ymessage {
      globalId
      body
    }
    worksomeTimesheet {
      id
      overtimeHours
      regularHours
      overtimes(first: 1000, page: 1) {
        data {
          id
          type
          duration
          rate
          rateMultiplier
          total
          createdAt
          updatedAt
          timesheets(first: 1000, page: 1) {
            data {
              paymentRequests(first: 1000, page: 1) {
                data {
                  localId
                }
              }
            }
          }
        }
      }
    }
  }
`

export default {
  apollo: {
    bill: {
      skip() {
        return !this.readyToLoadData
      },
      query: gql`
        query Bill($localId: Int!) {
          me {
            globalId
            activeAccount {
              globalId
              ... on HasBills {
                bill(localId: $localId) {
                  freelancer {
                    localId
                    name
                    primaryProfile {
                      localId
                    }
                  }
                  globalId
                  localId
                  ...PopoverBillFragment
                }
              }
            }
          }
        }
        ${BILL_FRAGMENT}
      `,
      update(data) {
        this.loaded = true
        return data?.me?.activeAccount?.bill
      },
      variables() {
        return {
          localId: this.localId,
        }
      },
      // fetchPolicy: 'cache-only',
    },
  },
  fragment: BILL_FRAGMENT,
  components: { BaseLabel, GhostComponent },
  props: {
    localId: {
      type: Number,
      default: 0,
    },
    globalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      readyToLoadData: false,
      loaded: false,
      bill: {},
    }
  },
  computed: {
    title() {
      return `${$t('Bill')} ${this.bill?.billNumber || ''}`
    },
    timesheet() {
      return this.bill.worksomeTimesheet || null
    },
    hours() {
      return this.timesheet?.regularHours || this.bill.hours || 0
    },
    overtimeHours() {
      return this.timesheet?.overtimeHours || 0
    },
    moment: () => moment,
    vatTerminology() {
      return vatTerminology
    },
  },
}
</script>

<style scoped></style>
